import { BaseService } from './BaseService';
import type { TenantDto, CreateTenantRequest, UpdateTenantRequest, PagedResult } from '../types/tenant';

/**
 * Tenant service for handling tenant operations.
 */
export class TenantService extends BaseService {
  /**
   * Gets a paginated list of tenants.
   */
  async getTenants(params?: {
    pageNumber?: number;
    pageSize?: number;
    searchTerm?: string;
    isActive?: boolean;
  }): Promise<PagedResult<TenantDto>> {
    const queryParams = new URLSearchParams();
    if (params?.pageNumber) queryParams.append('pageNumber', params.pageNumber.toString());
    if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params?.searchTerm) queryParams.append('searchTerm', params.searchTerm);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());

    const url = `/tenants${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.get<PagedResult<TenantDto>>(url);
  }

  /**
   * Gets a tenant by ID.
   */
  async getTenant(id: string): Promise<TenantDto> {
    return this.get<TenantDto>(`/tenants/${id}`);
  }

  /**
   * Gets the current tenant from context.
   */
  async getCurrentTenant(): Promise<TenantDto> {
    return this.get<TenantDto>('/tenants/current');
  }

  /**
   * Gets all tenants that the current user has access to.
   */
  async getUserTenants(): Promise<TenantDto[]> {
    return this.get<TenantDto[]>(`/tenants/mine`);
  }

  /**
   * Creates a new tenant.
   */
  async createTenant(data: CreateTenantRequest): Promise<TenantDto> {
    return this.post<TenantDto>('/tenants', data);
  }

  /**
   * Updates an existing tenant.
   */
  async updateTenant(id: string, data: UpdateTenantRequest): Promise<TenantDto> {
    return this.put<TenantDto>(`/tenants/${id}`, data);
  }

  /**
   * Deletes a tenant.
   */
  async deleteTenant(id: string): Promise<void> {
    return this.delete<void>(`/tenants/${id}`);
  }
}

export const tenantService = new TenantService();
